/* eslint-disable import/no-default-export */
import fStr from 'string-template';
import * as utilMod from './util';
import { parseLocale, mergeDictionaries, getDictInformation } from './util';
import type { Dict } from './util';
import { isString } from 'lodash';

const NO_LOCALE = "??_??";
const EMPTY_DICT = {};

export const util = utilMod;

export type Locale = {
    lang: string,
    country: string,
    toString: () => string
};

export type OptionParam = any;

export class Translator {
    lang: string;
    dict: Dict;
    fallbackDict: Dict;
    logMissing: boolean;
    boundTR: (key: string, pluralOrOptionsParam?: OptionParam, optionsParam?: OptionParam) => string;
    // tr: (key: string, pluralOrOptionsParam?: OptionParam, optionsParam?: OptionParam) => string;
    constructor(lang: string, dictObject: Dict, fallbackDict?: Dict ) {
        this.lang = lang;
        this.dict = dictObject;
        this.fallbackDict = fallbackDict;
        this.logMissing = false;
        this.boundTR = this.tr.bind(this);
    }

    // Merge dict values on top of the current dict
    mergeAdditionalDict(dict: Dict): void {
        this.dict = mergeDictionaries(this.dict, dict);
    }
    
    // File format (Generated bu the ts-jspotoolchain project:
    //   JSON format extracted using po2json from PO files.
    /*
    {
        "Annulé": [ // Key
            null, // Plural sub key
            "Cancel" // Translation 
        ],
        "Vous avez {qty} billet dans votre panier.": [ // Key
            "Vous avez {qty} billets dans votre panier", // Plural sub key
            "", // Translation
            "" // Plural translation
        ],
    }
    */
    
    tr(key: string, pluralOrOptionsParam: OptionParam, optionsParam: OptionParam): string {
        const options = getOptions(pluralOrOptionsParam, optionsParam);
        
        // Try in the main dict
        let trVal = readStringFromDict(this.dict, key, pluralOrOptionsParam, optionsParam);

        // If not found Check in the fallback dict
        if(trVal == null && this.fallbackDict !== undefined) {
            if(this.logMissing)
                console.log(`Falling back from ${this.lang} to ${getDictInformation(this.fallbackDict).language} dict for key: ${key}`);
            trVal = readStringFromDict(this.fallbackDict, key, pluralOrOptionsParam, optionsParam);
        }

        // If still not found, return the key
        if(trVal == null) {
            if(this.logMissing)
                console.log(`Missing translation for ${this.lang}: ${key}`);

            const pluralKey = getPluralKey(pluralOrOptionsParam);
            // If plural supported
            if(pluralKey !== undefined && options !== undefined && options.n != undefined && options.n > 1) 
                trVal = pluralKey; // Plural!

            if(trVal == null)
                trVal = key; // Singular!
        }
        
        if(options != undefined)
            trVal = fStr(trVal, options); // Format!

        // Ret val or key if val not found
        return trVal;
    }
}


export class TranslatorManager {
    fallbackDict: Dict;
    translators: { [key: string]: Translator };
    currentTranslator: Translator | null;
    emptyTranslator: Translator;
    boundTR: (key: string, pluralOrOptionsParam?: OptionParam, optionsParam?: OptionParam) => string;
    fallbackTR: Translator;
    constructor() {
        this.fallbackDict = undefined;
        this.translators = {};
        this.emptyTranslator = new Translator(NO_LOCALE, EMPTY_DICT);
        this.currentTranslator = null; // This is the "static" current culture for when the package is used in a single client place like in a browser or in a simple script.
        this.boundTR = this.tr.bind(this);
    }

    // Use this when you code wants to deal with almost-static stuff like in a browser.
    setLocale(lang: string): void {
        this.currentTranslator = this.getLocaleTR(lang);
    }

    // Dict: Gettext file in JSON format created by the npm module "po2json"
    registerDict(locale: string, dict: Dict): void {
        const loc = parseLocale(locale);
        if(loc == null)
            throw "You must pass in a valid locale name. Received: " + locale;
        
        let tr = this.translators[locale];

        // If the translator already exists, merge our new dictionayr in it.
        if(tr != undefined){
            tr.mergeAdditionalDict(dict);
        } else { // We need a brand new translator for this locale
            if(this.fallbackDict == undefined){ // If first
                this.fallbackDict = dict; // The first dict is the global fallback
                tr = new Translator(locale, dict);
                this.fallbackTR = tr;

                this.setLocale(locale); // Set this first dict as the current language
            }
            else
                tr = new Translator(locale, dict, this.fallbackDict); // Fallbacks could be by lang first
            
            this.translators[locale] = tr;
            if(this.translators[loc.lang] == undefined) // If we don't have a default for this lang yet
                this.translators[loc.lang] = tr; // Add a default for the lang. (And not only for the specific country)

        }
    }

    // Use this when you code wants to deal with almost-static stuff like in a browser.
    getCurrentLocaleTR(): Translator {
        return this.currentTranslator != null ? this.currentTranslator : this.emptyTranslator; // We have to make it works even though it's not initialized because some module just won't load in some cases where we don't need the translation. Translation is often used statically in the modules.
    }
    
    // Use this when you code wants to deal with almost-static stuff like in a browser.
    // This one is important for the static controls since it is not locked on a single language, it will follow the right translator on each call.
    tr(key: string, pluralOrOptionsParam?: OptionParam, optionsParam?: OptionParam): string {
        return this.getCurrentLocaleTR().tr(key, pluralOrOptionsParam, optionsParam);            
    }
    
    getLocaleTR(locale: string): Translator{
        const loc = parseLocale(locale);
        if(loc == null) return this.fallbackTR;
        
        let tr = this.translators[locale]; // Get for the lang and country (fr-FR)
        if(tr == undefined) // If not found
            tr = this.translators[loc.lang]; // Get for the lang only (fr)

        if(tr == undefined)
            tr = this.fallbackTR; // If nothing found then fall back

        return tr;
    }
}
// eslint-disable-next-line import/no-default-export
export default (new TranslatorManager()); // Set a default manager 

function getPluralKey(pluralOrOptionsParam) { return isString(pluralOrOptionsParam) ? pluralOrOptionsParam : undefined; }
function getOptions(pluralOrOptionsParam, optionsParam) { return optionsParam || (!isString(pluralOrOptionsParam) ? pluralOrOptionsParam : undefined); }

// The function that find the correct anwser in a specific dictionary
function readStringFromDict(dict: Dict, key: string, pluralOrOptionsParam: OptionParam, optionsParam: OptionParam): string | null {
    const pluralKey = getPluralKey(pluralOrOptionsParam);
    const options = getOptions(pluralOrOptionsParam, optionsParam);
    let trVal = undefined;

    // valKit: Array in which:
    //   [0] is the plural Key (Null if no plural),
    //   [1] is the singular translation,
    //   [2] is the plural translation
    
    // Check in the main dict
    const valKit = dict[key]; 
    if(valKit == undefined)
        return null;
    
    // If plural supported
    if(pluralKey !== undefined && options !== undefined && options.n != undefined && options.n > 1) 
        trVal = valKit[2]; // Plural!
    
    if(trVal == undefined)
        trVal = valKit[1]; // Singular!

    if(trVal != undefined && trVal.length == 0)
        return null;
    
    return trVal;
}
